import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Spin, Table, Tag } from "@/frontend/components/common";
import { fixtureService, fixtureStatusService } from "@/frontend/services";
import type {
  FixtureResponseDTO,
  FixtureStatusResponseDTO,
} from "@/shared/dto";
import type { ColumnsType } from "antd/es/table";
import { FaClock, FaFutbol } from "react-icons/fa";
import type { SeasonInfo } from "../types";

interface LiveFixturesTabProps {
  currentSeason?: SeasonInfo;
}

const LIVE_WINDOW_MINUTES = 90;

function formatClock(value: Date | string) {
  return new Date(value).toLocaleTimeString("es-CO", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getLiveMinute(matchDate: Date | string) {
  const startedAt = new Date(matchDate).getTime();
  if (Number.isNaN(startedAt)) return null;

  const elapsed = Math.floor((Date.now() - startedAt) / 60000);
  if (elapsed < 0 || elapsed > LIVE_WINDOW_MINUTES) return null;

  return Math.min(elapsed + 1, LIVE_WINDOW_MINUTES);
}

function hasRealScore(fixture: FixtureResponseDTO) {
  return fixture.homeScore != null && fixture.awayScore != null;
}

function isFinishedStatus(
  fixture: FixtureResponseDTO,
  statusMap: Map<string, FixtureStatusResponseDTO>,
) {
  return Boolean(statusMap.get(fixture.statusId ?? "")?.isFinal);
}

export function LiveFixturesTab({ currentSeason }: LiveFixturesTabProps) {
  const [fixtures, setFixtures] = useState<FixtureResponseDTO[]>([]);
  const [statuses, setStatuses] = useState<FixtureStatusResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchLiveFixtures = useCallback(async () => {
    if (!currentSeason?.id) return;

    const now = new Date();
    const windowStart = new Date(now.getTime() - LIVE_WINDOW_MINUTES * 60000);

    setLoading(true);
    try {
      const response = await fixtureService.getBySeasonId(currentSeason.id, {
        pageSize: 100,
        dateFrom: windowStart.toISOString(),
        dateTo: now.toISOString(),
        direction: "forward",
      });

      if (response.success && response.data) {
        setFixtures(response.data.data || []);
      }
    } catch (error) {
      console.error("Error al cargar partidos en vivo:", error);
    } finally {
      setLoading(false);
    }
  }, [currentSeason?.id]);

  useEffect(() => {
    fetchLiveFixtures();
    const interval = window.setInterval(fetchLiveFixtures, 30000);
    return () => window.clearInterval(interval);
  }, [fetchLiveFixtures]);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await fixtureStatusService.getPaginate({
          page: 1,
          pageSize: 50,
        });
        if (response.success && response.data) {
          setStatuses(response.data.data || []);
        }
      } catch (error) {
        console.error("Error al cargar estados de partido:", error);
      }
    };

    fetchStatuses();
  }, []);

  const statusMap = useMemo(() => {
    const map = new Map<string, FixtureStatusResponseDTO>();
    statuses.forEach((status) => map.set(status.id, status));
    return map;
  }, [statuses]);

  const liveFixtures = useMemo(
    () =>
      fixtures
        .filter(
          (fixture) =>
            getLiveMinute(fixture.matchDate) !== null &&
            !isFinishedStatus(fixture, statusMap),
        )
        .sort(
          (a, b) =>
            new Date(a.matchDate).getTime() - new Date(b.matchDate).getTime(),
        ),
    [fixtures, statusMap],
  );

  const columns: ColumnsType<FixtureResponseDTO> = [
    {
      title: "Min",
      key: "minute",
      width: 90,
      align: "center",
      render: (_, record) => {
        const minute = getLiveMinute(record.matchDate);
        return (
          <Tag color="red" className="!m-0">
            {minute ? `${minute}'` : "En vivo"}
          </Tag>
        );
      },
    },
    {
      title: "Inicio",
      dataIndex: "matchDate",
      key: "matchDate",
      width: 110,
      render: (date: Date) => (
        <span className="font-medium text-gray-700">{formatClock(date)}</span>
      ),
    },
    {
      title: "Equipo Local",
      key: "homeTeam",
      render: (_, record) => (
        <div className="flex items-center justify-center gap-2 font-medium">
          {record.homeTeam?.country?.flag && (
            <img
              src={record.homeTeam.country.flag}
              alt=""
              className="h-3 w-4 rounded object-cover"
            />
          )}
          {record.homeTeam?.name ?? "-"}
        </div>
      ),
    },
    {
      title: "Marcador",
      key: "score",
      width: 130,
      align: "center",
      render: (_, record) => (
        <span className="text-lg font-bold text-gray-900">
          {hasRealScore(record) ? `${record.homeScore} - ${record.awayScore}` : "-"}
        </span>
      ),
    },
    {
      title: "Equipo Visitante",
      key: "awayTeam",
      render: (_, record) => (
        <div className="flex items-center justify-center gap-2 font-medium">
          {record.awayTeam?.country?.flag && (
            <img
              src={record.awayTeam.country.flag}
              alt=""
              className="h-3 w-4 rounded object-cover"
            />
          )}
          {record.awayTeam?.name ?? "-"}
        </div>
      ),
    },
    {
      title: "Estado",
      key: "status",
      width: 130,
      align: "center",
      render: (_, record) => {
        const status = statusMap.get(record.statusId ?? "");
        return (
          <Tag color={status?.isFinal ? "green" : "processing"} className="!m-0">
            {status?.name ?? record.statusId ?? "En vivo"}
          </Tag>
        );
      },
    },
  ];

  if (!currentSeason) {
    return (
      <div className="py-4">
        <Card className="shadow-sm">
          <div className="py-12 text-center">
            <FaClock className="mx-auto mb-4 text-5xl text-gray-300" />
            <p className="text-gray-500">No hay una temporada actual seleccionada</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="py-4">
      <Card className="shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-red-600">
              <FaFutbol />
            </span>
            <div>
              <h3 className="m-0 text-base font-semibold text-gray-900">
                Partidos en vivo
              </h3>
              <p className="m-0 text-xs text-gray-500">
                Desde el comienzo del partido hasta el minuto 90
              </p>
            </div>
          </div>
          <Tag color="red" className="!m-0">
            {liveFixtures.length} en vivo
          </Tag>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spin size="large" tip="Cargando partidos en vivo..." />
          </div>
        ) : liveFixtures.length > 0 ? (
          <Table
            columns={columns}
            dataSource={liveFixtures}
            rowKey="id"
            size="middle"
            pagination={false}
          />
        ) : (
          <div className="py-12 text-center">
            <FaClock className="mx-auto mb-4 text-5xl text-gray-300" />
            <p className="text-gray-500">
              No hay partidos en vivo en este momento
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
